import { useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useRouter } from 'next/router';

import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { AuthenticatedUserProfile } from 'src/types/AuthenticatedUserProfile';

import findMenuNestedItems from 'src/utils/findMenuNestedItems';

import SlideOut from 'src/components/basicContent/_SlideOut/_SlideOut';

import MultiLevelMenu from '../../_MultiTierMenu/_MultiTierMenu';
import SubMenu from '../../_MultiTierMenu/SubMenu/SubMenu';
import { HeaderFields } from '../../HeaderFields';
import { NavigationMenu } from '../../NavigationMenu';
import LoggedInButtons from '../_LoggedInButtons/_LoggedInButtons';
import LoggedOutButtons from '../_LoggedOutButtons/_LoggedOutButtons';

import MenuHeading from './_MenuHeading/_MenuHeading';
import PersonalisedLinks from './_PersonalisedLinks/_PersonalisedLinks';

import styles from './_HamburgerMenu.module.css';

type HamburgerProps = {
	fields: HeaderFields & {
		NavigationMenu: NavigationMenu;
	};
	authenticatedUserProfile?: AuthenticatedUserProfile;
	menuState: boolean;
	setMenuState: (arg0: boolean) => void;
};

export default function HamburgerMenu(props: HamburgerProps) {
	const router = useRouter();

	const [showSubMenu, setShowSubMenu] = useState(false);
	const [selectedMenuItemTitle, setSelectedMenuItemTitle] = useState<Field<string>>();
	const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);

	const transitionDuration = 200;

	useEffect(() => {
		const closeMenu = () => {
			props.setMenuState(false);
			setShowSubMenu(false);
		};

		router.events.on('routeChangeComplete', closeMenu);

		return () => {
			router.events.off('routeChangeComplete', closeMenu);
		};
	}, [props, router.events]);

	useEffect(() => {
		setShowSubMenu(!props.menuState);
	}, [props.menuState]);

	return (
		<SlideOut slideOutState={props.menuState} setSlideOutState={props.setMenuState} onExit={() => setShowSubMenu(false)}>
			<TransitionGroup className={styles.menuContent}>
				<CSSTransition key={0} in={showSubMenu} timeout={transitionDuration} classNames={{ ...styles }}>
					<div className={styles.menuContentItem}>
						{!showSubMenu && props.authenticatedUserProfile && (
							<>
								<PersonalisedLinks fields={props.fields} showWelcomeMessage authenticatedUserProfile={props.authenticatedUserProfile} />
								<hr className={styles.divider} />
							</>
						)}

						{props.fields?.NavigationMenu?.items && (
							<div className={styles.hamburgerMenu}>
								<MenuHeading
									menuTitle={props.fields?.MobileMenuTitle}
									selectedMenuItemTitle={selectedMenuItemTitle}
									showSubMenu={showSubMenu}
									setSelectedMenuItemTitle={(value) => setSelectedMenuItemTitle(value)}
									clearMenuSelection={(value) => setShowSubMenu(value)}
								/>

								{!showSubMenu ? (
									<MultiLevelMenu
										NavigationMenu={props.fields?.NavigationMenu}
										className={styles.showMenu}
										setSelectedMenuItemTitle={(value) => setSelectedMenuItemTitle(value)}
										setShowSubMenu={(value) => setShowSubMenu(value)}
									/>
								) : (
									<SubMenu
										subMenuItems={props.fields?.NavigationMenu?.items?.find((item) => item.Title.value === selectedMenuItemTitle?.value)}
										activeSubMenu={activeSubMenu}
										nestedItem={
											props.fields?.NavigationMenu?.items
												?.map((item) => findMenuNestedItems(item, router.asPath))
												.find((result) => result.active)?.nestedItems
										}
										showNavigationLinks
										setActiveSubMenu={(value) => setActiveSubMenu(value)}
									/>
								)}
							</div>
						)}

						{!showSubMenu && (
							<>
								<hr className={styles.divider} />

								<div className={styles.buttonContainer}>
									<LoggedInButtons
										menuTriggerId="menu-trigger"
										LogOutText={props.fields?.LogOutText}
										authenticatedUserProfile={props.authenticatedUserProfile}
									/>

									<LoggedOutButtons fields={props.fields} authenticatedUserProfile={props.authenticatedUserProfile} />
								</div>
							</>
						)}
					</div>
				</CSSTransition>
			</TransitionGroup>
		</SlideOut>
	);
}
